import { MfaFactor, experience } from '@logto/schemas';
import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Route, Routes, BrowserRouter } from 'react-router-dom';

import { handleSearchParametersData } from '@/shared/utils/search-parameters';

import AppLayout from './Layout/AppLayout';
import AppBoundary from './Providers/AppBoundary';
import CaptchaContextProvider from './Providers/CaptchaContextProvider';
import ChunkLoadBoundary from './Providers/ChunkLoadBoundary';
import LoadingLayerProvider from './Providers/LoadingLayerProvider';
import PageContextProvider from './Providers/PageContextProvider';
import SettingsProvider from './Providers/SettingsProvider';
import UserInteractionContextProvider from './Providers/UserInteractionContextProvider';
import DevelopmentTenantNotification from './containers/DevelopmentTenantNotification';
// Eager: it wraps the first-paint routes below, so a lazy chunk would delay them.
import StepUpEntryGuard from './containers/StepUpEntryGuard';
// Eager imports below are the first-paint pages (sign-in / register critical path);
// the heavy below-the-fold pages are lazy()-split into the `const` block further down.
import ErrorPage from './pages/ErrorPage';
import ForgotPassword from './pages/ForgotPassword';
import IdentifierRegister from './pages/IdentifierRegister';
import IdentifierSignIn from './pages/IdentifierSignIn';
import Register from './pages/Register';
import RegisterPassword from './pages/RegisterPassword';
import SignIn from './pages/SignIn';
import SignInPassword from './pages/SignInPassword';
import VerificationCode from './pages/VerificationCode';
import { queryClient } from './query-client';
import { LoadingScreen } from './shared/components/LoadingLayer';
import { UserMfaFlow } from './types';
import './index.css';

/* ── Lazy: heavy / below-first-paint pages ──
   MFA, consent, device flow, SSO, social, step-up, account-switch etc. Most users
   never reach these, and several are large (WebAuthn, crop, etc.). Code-splitting
   them keeps them out of the initial bundle and loads on demand. */
const Callback = lazy(async () => import('./pages/Callback'));
const Consent = lazy(async () => import('./pages/Consent'));
const Continue = lazy(async () => import('./pages/Continue'));
const Device = lazy(async () => import('./pages/Device'));
const DeviceSuccess = lazy(async () => import('./pages/Device/Success'));
const DirectSignIn = lazy(async () => import('./pages/DirectSignIn'));
const MfaBinding = lazy(async () => import('./pages/MfaBinding'));
const BackupCodeBinding = lazy(async () => import('./pages/MfaBinding/BackupCodeBinding'));
const EmailMfaBinding = lazy(async () => import('./pages/MfaBinding/EmailMfaBinding'));
const PhoneMfaBinding = lazy(async () => import('./pages/MfaBinding/PhoneMfaBinding'));
const TotpBinding = lazy(async () => import('./pages/MfaBinding/TotpBinding'));
const WebAuthnBinding = lazy(async () => import('./pages/MfaBinding/WebAuthnBinding'));
const MfaOnboarding = lazy(async () => import('./pages/MfaOnboarding'));
const MfaVerification = lazy(async () => import('./pages/MfaVerification'));
const MfaVerificationGuard = lazy(async () => import('./containers/MfaVerificationGuard'));
const BackupCodeVerification = lazy(
  async () => import('./pages/MfaVerification/BackupCodeVerification')
);
const EmailVerificationCode = lazy(
  async () => import('./pages/MfaVerification/EmailVerificationCode')
);
const PhoneVerificationCode = lazy(
  async () => import('./pages/MfaVerification/PhoneVerificationCode')
);
const TotpVerification = lazy(async () => import('./pages/MfaVerification/TotpVerification'));
const WebAuthnVerification = lazy(
  async () => import('./pages/MfaVerification/WebAuthnVerification')
);
const OneTimeToken = lazy(async () => import('./pages/OneTimeToken'));
const OneTimeTokenErrorPage = lazy(async () => import('./pages/OneTimeToken/Error'));
const PasskeySetup = lazy(async () => import('./pages/PasskeySetup'));
const ResetPassword = lazy(async () => import('./pages/ResetPassword'));
const ResetPasswordLanding = lazy(async () => import('./pages/ResetPasswordLanding'));
const SignInPasskeyVerification = lazy(async () => import('./pages/SignInPasskeyVerification'));
const SignInVerificationMethods = lazy(async () => import('./pages/SignInVerificationMethods'));
const SingleSignOnConnectors = lazy(async () => import('./pages/SingleSignOnConnectors'));
const SingleSignOnEmail = lazy(async () => import('./pages/SingleSignOnEmail'));
const SingleSignOnLanding = lazy(async () => import('./pages/SingleSignOnLanding'));
const SocialLanding = lazy(async () => import('./pages/SocialLanding'));
const SocialLinkAccount = lazy(async () => import('./pages/SocialLinkAccount'));
const SocialSignInWebCallback = lazy(async () => import('./pages/SocialSignInWebCallback'));
const StepUpGuard = lazy(async () => import('./containers/StepUpGuard'));
const StepUpRecoveryGuard = lazy(async () => import('./containers/StepUpRecoveryGuard'));
const StepUp = lazy(async () => import('./pages/StepUp'));
const StepUpPassword = lazy(async () => import('./pages/StepUp/Password'));
const SubjectMismatch = lazy(async () => import('./pages/StepUp/SubjectMismatch'));
const StepUpVerificationCode = lazy(async () => import('./pages/StepUp/VerificationCode'));
const SwitchAccount = lazy(async () => import('./pages/SwitchAccount'));
const TrustedDevice = lazy(async () => import('./pages/TrustedDevice'));
const UnknownSession = lazy(async () => import('./pages/UnknownSession'));

handleSearchParametersData();

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <PageContextProvider>
            <SettingsProvider>
              <UserInteractionContextProvider>
                <CaptchaContextProvider>
                  <DevelopmentTenantNotification />
                  <AppBoundary>
                    <ChunkLoadBoundary fallback={<ErrorPage />}>
                      <Suspense fallback={<LoadingScreen isDelayed />}>
                        <Routes>
                          <Route element={<LoadingLayerProvider />}>
                            <Route path="callback/:connectorId" element={<Callback />} />
                            {/* Step-up callbacks recover the mode before consuming upstream proofs. */}
                            <Route element={<StepUpRecoveryGuard />}>
                              <Route
                                path="callback/social/:connectorId"
                                element={<SocialSignInWebCallback />}
                              />
                            </Route>
                            {/* Normal sign-in entries are blocked while a step-up pins the subject. */}
                            <Route element={<StepUpEntryGuard />}>
                              <Route path="direct/:method/:target?" element={<DirectSignIn />} />
                              <Route
                                path={experience.routes.oneTimeToken}
                                element={<OneTimeToken />}
                              />
                            </Route>
                            <Route element={<AppLayout />}>
                              <Route
                                path={`${experience.routes.oneTimeToken}/error`}
                                element={<OneTimeTokenErrorPage />}
                              />
                              <Route path="unknown-session" element={<UnknownSession />} />

                              <Route
                                path={experience.routes.accountSuspended}
                                element={
                                  <ErrorPage
                                    isNavbarHidden
                                    title="error.account_suspended"
                                    message="error.account_suspended_description"
                                    primaryAction={{
                                      title: 'description.back_to_sign_in',
                                      to: `/${experience.routes.signIn}`,
                                      replace: true,
                                    }}
                                  />
                                }
                              />

                              {/*
                               * Normal entry routes. A pure step-up pins its subject, so every route that
                               * could identify, create, or reset an account is blocked before its form
                               * renders. Core enforces the same restriction; the guard only prevents a
                               * form that can never complete.
                               */}
                              <Route element={<StepUpEntryGuard />}>
                                <Route
                                  path={experience.routes.switchAccount}
                                  element={<SwitchAccount />}
                                />

                                {/* Sign-in */}
                                <Route path={experience.routes.signIn}>
                                  <Route index element={<SignIn />} />
                                  <Route path="password" element={<SignInPassword />} />
                                  <Route path="passkey" element={<SignInPasskeyVerification />} />
                                  <Route
                                    path="verification-methods"
                                    element={<SignInVerificationMethods />}
                                  />
                                </Route>

                                {/* Register */}
                                <Route path={experience.routes.register}>
                                  <Route index element={<Register />} />
                                  <Route path="password" element={<RegisterPassword />} />
                                </Route>

                                {/* Forgot password */}
                                <Route path="forgot-password">
                                  <Route index element={<ForgotPassword />} />
                                  <Route path="reset" element={<ResetPassword />} />
                                </Route>

                                {/* Single sign-on */}
                                <Route path={experience.routes.sso}>
                                  {/* Single sign-on first screen landing page */}
                                  <Route index element={<SingleSignOnLanding />} />
                                  <Route path="email" element={<SingleSignOnEmail />} />
                                  <Route path="connectors" element={<SingleSignOnConnectors />} />
                                </Route>

                                {/*
                                 * Identifier sign-in (first screen)
                                 * The first screen which only display specific identifier-based sign-in methods to users
                                 */}
                                <Route
                                  path={experience.routes.identifierSignIn}
                                  element={<IdentifierSignIn />}
                                />

                                {/*
                                 * Identifier register (first screen)
                                 * The first screen which only display specific identifier-based registration methods to users
                                 */}
                                <Route
                                  path={experience.routes.identifierRegister}
                                  element={<IdentifierRegister />}
                                />

                                {/*
                                 * Reset password (first screen)
                                 * The first screen which allow users to directly access the password reset page
                                 */}
                                <Route
                                  path={experience.routes.resetPassword}
                                  element={<ResetPasswordLanding />}
                                />
                              </Route>

                              {/* Create passkey for sign-in */}
                              <Route path="create-passkey" element={<PasskeySetup />} />

                              {/* Trusted device */}
                              <Route
                                path={experience.routes.trustedDevice}
                                element={<TrustedDevice />}
                              />

                              {/* Step-up establishment and enrollment reuse the existing pages. */}
                              <Route element={<StepUpRecoveryGuard />}>
                                {/* Passwordless verification code */}
                                <Route
                                  path=":flow/verification-code"
                                  element={<VerificationCode />}
                                />

                                {/* Mfa onboarding page. Prompt users to turn on 2-step verification. */}
                                <Route path="mfa-onboarding" element={<MfaOnboarding />} />

                                {/* Mfa binding */}
                                <Route path={UserMfaFlow.MfaBinding}>
                                  <Route index element={<MfaBinding />} />
                                  <Route path={MfaFactor.TOTP} element={<TotpBinding />} />
                                  <Route path={MfaFactor.WebAuthn} element={<WebAuthnBinding />} />
                                  <Route
                                    path={MfaFactor.BackupCode}
                                    element={<BackupCodeBinding />}
                                  />
                                  <Route
                                    path={MfaFactor.EmailVerificationCode}
                                    element={<EmailMfaBinding />}
                                  />
                                  <Route
                                    path={MfaFactor.PhoneVerificationCode}
                                    element={<PhoneMfaBinding />}
                                  />
                                </Route>

                                {/* Continue set up missing profile */}
                                <Route path="continue">
                                  <Route index element={<Continue />} />
                                  <Route path=":method" element={<Continue />} />
                                </Route>
                              </Route>

                              {/* Mfa verification: shared with step-up, which recovers its
      interaction state via MfaVerificationGuard. */}
                              <Route
                                path={UserMfaFlow.MfaVerification}
                                element={<MfaVerificationGuard />}
                              >
                                <Route index element={<MfaVerification />} />
                                <Route path={MfaFactor.TOTP} element={<TotpVerification />} />
                                <Route
                                  path={MfaFactor.WebAuthn}
                                  element={<WebAuthnVerification />}
                                />
                                <Route
                                  path={MfaFactor.BackupCode}
                                  element={<BackupCodeVerification />}
                                />
                                <Route
                                  path={MfaFactor.EmailVerificationCode}
                                  element={<EmailVerificationCode />}
                                />
                                <Route
                                  path={MfaFactor.PhoneVerificationCode}
                                  element={<PhoneVerificationCode />}
                                />
                              </Route>

                              {/*
                               * Step-up: an authenticated session proves the missing assurance.
                               * The guard loads the server-driven context, the landing dispatches
                               * on it, and the pinned-user first-factor pages verify the pinned
                               * subject.
                               */}
                              <Route path={experience.routes.stepUp} element={<StepUpGuard />}>
                                <Route index element={<StepUp />} />
                                <Route path="password" element={<StepUpPassword />} />
                                <Route
                                  path="verification-code/:type"
                                  element={<StepUpVerificationCode />}
                                />
                                <Route path="subject-mismatch" element={<SubjectMismatch />} />
                              </Route>

                              {/* Social sign-in pages */}
                              <Route path="social">
                                <Route path="link/:connectorId" element={<SocialLinkAccount />} />
                                <Route path="landing/:connectorId" element={<SocialLanding />} />
                              </Route>

                              {/* Consent */}
                              <Route path="consent" element={<Consent />} />

                              {/* Device flow */}
                              <Route path={experience.routes.device} element={<Device />} />
                              <Route
                                path={`${experience.routes.device}/success`}
                                element={<DeviceSuccess />}
                              />

                              <Route path="*" element={<ErrorPage />} />
                            </Route>
                          </Route>
                        </Routes>
                      </Suspense>
                    </ChunkLoadBoundary>
                  </AppBoundary>
                </CaptchaContextProvider>
              </UserInteractionContextProvider>
            </SettingsProvider>
          </PageContextProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
